import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { useModalManager } from 'components/common/Modal';
import SysadminTable from '../../../common/SysadminTable';
import { ModalButton, sortedMaterials } from '../Utils';
import Row from '../LessonMaterialsTable/Row';
import Form from '../LessonMaterialsTable/Form';

const baseColumns = [
  { text: '', className: 'image' },
  { text: 'Name', className: 'name' },
  { text: 'Quantity', className: 'quantity' },
  { text: 'Used in', className: 'used-in' },
  { text: 'Source', className: 'program-kit-material' },
  { text: 'Safety Information', className: 'safety-information' },
  { text: 'Actions', className: 'actions' },
];

const adminColumns = [
  { text: '', className: 'image' },
  { text: 'Name', className: 'name' },
  { text: 'File Name', className: 'file-name', lock: true },
  { text: 'Quantity', className: 'quantity' },
  { text: 'Used in', className: 'used-in' },
  { text: 'Source', className: 'program-kit-material' },
  { text: 'State View', className: 'state-view', lock: true },
  { text: 'Actions', className: 'actions' },
];

type ProgramKitMaterial = {
  material_id: number;
  value: number;
  label: string;
};
type SelectOption = {
  label: string;
  value: number;
};
type Column = {
  className: string;
  lock?: boolean;
  text: string;
};
type UnitProjectMaterialsTableProps = {
  endpoint: string;
  isAdmin?: boolean;
  programKitMaterials: ProgramKitMaterial[];
  unitId: string;
  unitNotebooks: SelectOption[];
  unitSlideshows: SelectOption[];
};

type AddModalButtonProps = {
  distributions: any[];
  isAdmin: boolean;
  materialsForSelect: any[];
  measurementUnits: any[];
  onAdd: (material: any) => void;
  programKitMaterials: any[];
  unitNotebooks: any[];
  unitSlideshows: any[];
  unitId: string;
};

const AddModalButton = ({
  distributions,
  isAdmin,
  materialsForSelect,
  measurementUnits,
  onAdd,
  programKitMaterials,
  unitNotebooks,
  unitSlideshows,
  unitId,
}: AddModalButtonProps) => {
  const modalManager = useModalManager();

  if (!isAdmin) return null;

  return (
    <ModalButton
      newRecord
      buttonClass="btn btn--green mr10"
      buttonText="Add Material"
      closeModal={modalManager.close}
      distributions={distributions}
      FormComponent={Form}
      forSelect={materialsForSelect}
      modalClass="add-materials"
      modalHeader="Add Material"
      modalIsOpen={modalManager.isOpen}
      modelType="UnitProject"
      openModal={modalManager.open}
      programKitMaterials={programKitMaterials}
      unitId={unitId}
      unitNotebooks={unitNotebooks}
      units={measurementUnits}
      unitSlideshows={unitSlideshows}
      onAdd={onAdd}
    />
  );
};

const UnitProjectMaterialsTable = ({
  endpoint,
  isAdmin = false,
  programKitMaterials,
  unitId,
  unitNotebooks,
  unitSlideshows,
}: UnitProjectMaterialsTableProps) => {
  const columns = isAdmin ? adminColumns : baseColumns;
  const [isLoading, setIsLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [materialsForSelect, setMaterialsForSelect] = useState([]);
  const [measurementUnits, setMeasurementUnits] = useState([]);

  const handleRemove = (id) => {
    setMaterials(prev => prev.filter(material => material.id !== id));
  };

  useEffect(() => {
    setIsLoading(true);

    Axios
      .get(endpoint)
      .then((res) => {
        if (res.data && res.data.materials) {
          setMaterials(sortedMaterials(res.data.materials));
          setMaterialsForSelect(res.data.forSelect);
          setDistributions(res.data.distributions);
          setMeasurementUnits(res.data.units);
        }

        setIsLoading(false);
      });
  }, [unitId]);

  return (
    <SysadminTable
      columns={columns}
      containerClass="unit-materials"
      emptyMessage="No materials found."
      tableId="materials"
      isLoading={isLoading}
      buttonAbove={(
        <div className="h35 inline_block pos_fr">
          <AddModalButton
            distributions={distributions}
            isAdmin={isAdmin}
            materialsForSelect={materialsForSelect}
            measurementUnits={measurementUnits}
            programKitMaterials={programKitMaterials}
            unitId={unitId}
            unitNotebooks={unitNotebooks}
            unitSlideshows={unitSlideshows}
            onAdd={material => setMaterials([...materials, material])}
          />
        </div>
      )}
    >
      {materials.map(material => (
        <Row
          admin={isAdmin}
          key={material.id}
          modalHeader="Edit Unit Material"
          newRecord={false}
          removeRow={handleRemove}
          updateRow={() => {}}
          kitMaterialsUrl=""
          usedIn={material.used_in}
          forSelect={materialsForSelect}
          distributions={distributions}
          unitNotebooks={unitNotebooks}
          programKitMaterials={programKitMaterials}
          units={measurementUnits}
          {...material}
        />
      ))}
    </SysadminTable>
  );
};

export default UnitProjectMaterialsTable;
